import {
    cloneDeep,
    curry,
    flow,
    identity,
    keys,
    map,
    merge,
    zipObject,
} from 'lodash/fp';

import staticAppsFallback from '../static/apps-fallback.json';

const reject = response => {
    if (response.text) {
        return response.text().then(body => {
            const error = new Error(response.statusText);
            error.status = response.status;
            error.body = body;
            return Promise.reject(error);
        });
    }

    const error = new Error(response.statusText);
    error.status = response.status;
    error.body = '';
    return Promise.reject(error);
};

const authorizeFetch = (fetch = window.fetch, token) => (url, options = {}) => {
    const mergedOptions = merge(cloneDeep(options), {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return fetch(url, mergedOptions);
};

const jsonOrReject = response => response.ok ? response.json() : reject(response);

export const determineUrlSettings = (location, url) => {
    const isOnline = /\.rio\.cloud$/.test(location.host);
    const isProd = /start\.rio\.cloud$/.test(location.host);
    const isStaging = /test\.rio\.cloud$/.test(location.host);
    const urlIsAbsolute = /^https?:\/\//.test(url);
    const isOwnAccount = isOnline && (!isProd && !isStaging);

    return {
        isOnline,
        isOwnAccount,
        isProd,
        isStaging,
        urlIsAbsolute,
    };
};

const normalizeBucketName = location => flow(
    uri => {
        const {
            isOwnAccount,
            isStaging,
            urlIsAbsolute,
        } = determineUrlSettings(location, uri);

        if (urlIsAbsolute) {
            if (isStaging) {
                // Rewriting paths to stay in staging
                return uri.replace(
                    /^:\/\/start\.rio\.cloud/,
                    '://test.rio.cloud'
                );
            }

            return uri;
        }

        const relativeUri = uri;

        if (isOwnAccount) {
            return `https://start.rio.cloud${uri}`;
        }

        return relativeUri;
    },
    bucket => /\/$/.test(bucket) ? bucket : `${bucket}/`
);

export const getBucketFileUrl = curry(function(location, file, app) {
    return `${normalizeBucketName(location)(app.bucket)}${file}`;
});

const fetchResult = response => response.ok ? response.json() : null;

const mapFetchResults = flow(
    map(fetchResult)
);

const mergeMetas = (apps, metas) => {
    const zippedMetas = flow(
        map(meta => ({ meta })),
        zipObject(keys(apps))
    )(metas);

    return flow(
        cloneDeep,
        merge(zippedMetas)
    )(apps);
};

export const configureFetchApps = (fetch, location) => {
    // Note that we have to use the unauthenticated vanilla fetch here
    // and we catch fetch errors so we can use `Promise.all` to wait
    // for all promises to be done, even if rejected
    // https://gist.github.com/nhagen/a1d36b39977822c224b8
    const fetchIfAvailable = url => (
        fetch(url).catch(identity)
    );

    const mapAppsToFetches = flow(
        map(getBucketFileUrl(location)(`meta.json`)),
        map(fetchIfAvailable)
    );

    const fetchMetas = rawApps => {
        const promises = mapAppsToFetches(rawApps);

        return Promise.all(promises).then(responses => (
            Promise.all(mapFetchResults(responses)).then(metas => (
                mergeMetas(rawApps, metas)
            ))
        ));
    };

    const { isOnline } = determineUrlSettings(location, '/');

    const fetchFallback = fallback => () => {
        if (!isOnline && fallback) {
            console.warn(`AppRegistry is not reachable and we're not online, using configured fallback ${fallback}`);
            return fetch(fallback)
                .then(fetchResult)
                .then(rawApps => fetchMetas(rawApps))
                .catch(fetchFallback());
        } else if (!isOnline) {
            console.warn(`AppRegistry is not reachable and we're not online, using the static fallback should be ok`, staticAppsFallback);
            return fetchMetas(staticAppsFallback);
        }

        return Promise.reject(new Error(`AppRegistry is not reachable`));
    };

    const fetchApps = (endpoint = '/appregistry/apps', token, fallback = null) => {
        const authorizedFetch = authorizeFetch(fetch, token);

        return authorizedFetch(endpoint)
            .then(jsonOrReject)
            .then(rawApps => fetchMetas(rawApps))
            .catch(fetchFallback(fallback));
    };

    return {
        fetchApps,
    };
};
